import { PLAN_DEFINITIONS, limitLabel } from '@flexbyt/shared';
import { createFileRoute } from '@tanstack/react-router';
import { PageHero } from '../components/ui/hero.js';
import { FormsScene } from '../components/mascot/scenes.js';
import { FormPreview } from '../components/form/FormPreview.js';
import { RequestEmailPreview } from '../components/form/RequestEmailPreview.js';
import { ButtonAnchor, ButtonLink, SiteLink } from '../components/ui/button.js';
import { Measure, Section } from '../components/ui/layout.js';
import { Eyebrow, Heading, Mark, Text } from '../components/ui/type.js';
import { WIDGET_BUILDER_PATH } from '../content/widgets.js';
import { track } from '../lib/analytics.js';
import { breadcrumbSchema, jsonLd, seo } from '../lib/seo.js';
import { startUrl } from '../lib/site.js';

/**
 * The hosted review form, promoted.
 *
 * ===========================================================================
 * A second way to get reviews, not the main one
 * ===========================================================================
 * The site's whole positioning is import → manage → display — reviews you
 * already have, brought in from Google, Trustpilot and the rest. A form is the
 * other direction: reviews you do not have yet, asked for directly. It is real
 * and worth a page, but it is not the pitch, so this page stays out of
 * `PRIMARY_NAV` and off the homepage. It is reachable from the footer's
 * Product column and from the relevant bullet on `/manage-reviews`.
 *
 * ===========================================================================
 * The one idea worth a whole page
 * ===========================================================================
 * The same form can do either of two things with an answer, chosen per form:
 * collect it into the ReviewsJet library like anything else imported, or send
 * the reviewer straight to Google or Trustpilot instead. Most of the page
 * exists to explain that choice and when to make it — `/collect/forms` in the
 * product calls this `collectWithExternalSites`, and `FormPreview` draws both
 * states from the same default content the form itself starts with.
 *
 * ===========================================================================
 * Requests get a section, not a page of their own
 * ===========================================================================
 * `/collect/requests` — the dashboard's "email one customer" tool — is here
 * too, further down. It has nothing to say on its own: it is a delivery
 * mechanism for the link this page is already about, sharing the same monthly
 * allowance, so a second page promoting it would be re-explaining the form
 * with a different picture. `RequestEmailPreview` mirrors the dashboard's
 * own `EmailPreview` for the same reason `FormPreview` mirrors the form.
 */
export const Route = createFileRoute('/review-forms')({
  head: () => {
    const { meta, links } = seo({
      title: 'Review collection forms',
      description:
        'Collect reviews with a hosted form — into your library, or sent on to Google or Trustpilot — and ask for them yourself with a built-in request email.',
      path: '/review-forms',
    });

    return {
      meta,
      links,
      scripts: [
        {
          type: 'application/ld+json',
          children: jsonLd(
            breadcrumbSchema([
              { name: 'Home', path: '/' },
              { name: 'Review forms', path: '/review-forms' },
            ])
          ),
        },
      ],
    };
  },
  component: ReviewForms,
});

function ReviewForms() {
  const free = PLAN_DEFINITIONS.free.limits;

  return (
    <>
      {/* ------------------------------------------------------------ Hero */}
      <PageHero
        trail={[
          { name: 'Home', path: '/' },
          { name: 'Review forms', path: '/review-forms' },
        ]}
        art={<FormsScene />}
      >
        <Measure>
          <Eyebrow>Collect reviews</Eyebrow>
          <Heading level={1} size="hero" className="mt-4">
            Ask for the reviews you <span className="whitespace-nowrap"><Mark>don&rsquo;t have yet</Mark>.</span>
          </Heading>
          <Text size="lead" className="mt-6">
            Send a link — in an email, on a receipt, from a QR code — and every
            answer either lands straight in your ReviewsJet library, or goes
            straight to Google or Trustpilot instead. You choose which, per
            form.
          </Text>

          <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
            <ButtonAnchor
              href={startUrl('review-forms-hero')}
              size="lg"
              onClick={() =>
                track('hero_cta_clicked', { surface: 'review-forms-hero' })
              }
            >
              Start free
            </ButtonAnchor>
            <ButtonLink to="/manage-reviews" variant="secondary" size="lg">
              See the library it feeds
            </ButtonLink>
          </div>
        </Measure>
      </PageHero>

      {/* ------------------------------------------------------- Two modes */}
      <Section aria-labelledby="modes-heading">
        <Measure>
          <Heading level={2} id="modes-heading">
            One form, two jobs.
          </Heading>
          <Text className="mt-4 max-w-prose">
            Every form starts identical — same screens, same questions — and
            differs in exactly one setting: what happens to the &ldquo;write a
            review&rdquo; button on the welcome screen.
          </Text>
        </Measure>

        <div className="mt-10 grid gap-10 md:grid-cols-2">
          <div>
            <FormPreview mode="collect" />
            <p className="mt-5 text-title text-ink-950">Collect it here</p>
            <p className="mt-2 max-w-prose text-body-sm text-ink-600">
              The reviewer writes their review on the form. It lands in your
              ReviewsJet library exactly like an imported one — tag it,
              publish it, put it in a widget.
            </p>
          </div>

          <div>
            <FormPreview mode="redirect" />
            <p className="mt-5 text-title text-ink-950">
              Send them to Google or Trustpilot instead
            </p>
            <p className="mt-2 max-w-prose text-body-sm text-ink-600">
              The button becomes a choice of the sources you&rsquo;ve
              connected. The reviewer leaves and posts there — you get the
              rating on the listing that already matters to your search
              ranking, not a testimonial only your own site can show.
            </p>
          </div>
        </div>
      </Section>

      {/* ---------------------------------------------------- Why redirect */}
      <Section tone="raised" aria-labelledby="why-heading">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] lg:gap-16">
          <div>
            <Heading level={2} id="why-heading">
              Why give the review away.
            </Heading>
            <Text className="mt-4 max-w-prose">
              For most local businesses, a public rating on Google is worth
              more than a quote on their own site — it is what shows up next
              to the map pin before anyone reaches your homepage at all. A form
              that only collects reviews for you gets that trade-off backwards.
            </Text>
          </div>

          <div>
            <div className="border-l-2 border-ink-200 pl-5">
              <p className="text-caption font-semibold text-ink-950">
                Still worth collecting some yourself
              </p>
              <p className="mt-2 max-w-prose text-body-sm text-ink-600">
                A quote with a name and a photo does something a star rating
                cannot: it reads as a specific person&rsquo;s experience, on
                the page where somebody is already deciding. That is what the
                &ldquo;collect it here&rdquo; mode is for, and nothing stops
                you running one of each.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* -------------------------------------------------------- The link */}
      <Section aria-labelledby="link-heading">
        <Measure>
          <Heading level={2} id="link-heading">
            One link, no code.
          </Heading>
          <Text className="mt-4 max-w-prose">
            A form is a hosted page at its own address — nothing to embed,
            nothing to style to match your site, because the reviewer never
            sees your site. Put the link in a follow-up email, a receipt, a
            table tent, a QR code by the till.
          </Text>
        </Measure>
      </Section>

      {/* --------------------------------------------------------- Requests */}
      <Section tone="raised" aria-labelledby="requests-heading">
        <Measure>
          <Heading level={2} id="requests-heading">
            Or don&rsquo;t wait for them to find it.
          </Heading>
          <Text className="mt-4 max-w-prose">
            A form only works once somebody opens it. Send the ask yourself
            instead: type a customer&rsquo;s name and email, choose which form
            the button opens, and send. No campaign to build, no email
            platform to connect — one message, to one person, from you.
          </Text>
        </Measure>

        <div className="mt-10 grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,26rem)] lg:items-start">
          <ul className="order-2 space-y-6 lg:order-1">
            {[
              {
                title: 'Send yourself a test first',
                body: 'A copy lands in your own inbox before it reaches a customer — the only way to know the wording reads right.',
              },
              {
                title: 'Picks up where the form leaves off',
                body: 'The button opens whichever form you choose, in whichever mode you set it to — collected here, or sent on to Google or Trustpilot.',
              },
              {
                title: 'Shares the form’s allowance',
                body: `${limitLabel(free.reviewRequests)} a month between collection forms and request emails together on the free plan. Paid plans raise it.`,
              },
            ].map((item) => (
              <li key={item.title}>
                <p className="text-title text-ink-950">{item.title}</p>
                <p className="mt-2 max-w-prose text-body-sm text-ink-600">
                  {item.body}
                </p>
              </li>
            ))}
          </ul>

          <div className="order-1 lg:order-2">
            <RequestEmailPreview />
          </div>
        </div>

        <p className="mt-8 max-w-prose text-caption text-ink-500">
          See what every plan includes on{' '}
          <SiteLink
            to="/pricing"
            className="font-medium text-accent-600 underline decoration-accent-200 underline-offset-[3px] hover:text-accent-700"
          >
            full pricing
          </SiteLink>
          .
        </p>
      </Section>

      {/* ------------------------------------------------------------- CTA */}
      <Section tone="ink" size="base">
        <Measure>
          <Heading level={2} className="text-white">
            Set one up in a few minutes.
          </Heading>
          <Text className="mt-4 text-white/70">
            Free plan, no card. Import what you already have first, or start
            here and ask for the rest.
          </Text>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <ButtonAnchor
              href={startUrl('review-forms-close')}
              variant="inverse"
              size="lg"
              onClick={() =>
                track('hero_cta_clicked', { surface: 'review-forms-close' })
              }
            >
              Start free
            </ButtonAnchor>
            <ButtonLink
              to={WIDGET_BUILDER_PATH}
              variant="inverse-ghost"
              size="lg"
            >
              Build a widget first
            </ButtonLink>
          </div>
        </Measure>
      </Section>
    </>
  );
}
